/*
  component-klaviyo-form.js — reusable form handler + client-side validation.

  Drives any form carrying the data-attribute contract (see
  docs/klaviyo-integration-plan.md §6), with zero per-form JS:

    <form data-klaviyo-form="subscribe|event"
          data-klaviyo-metric="…"  (event mode)
          data-klaviyo-source="…"> (event mode)
    input[name] = email | first_name | last_name | full_name | phone | message | topic
    [data-klaviyo-consent]  — opt-in checkbox (required attr = must be ticked)
    [data-klaviyo-honeypot] — spam trap

  Validation runs ALWAYS (independent of Klaviyo): any field with the `required`
  attribute must be filled, email/phone formats are checked, and the native
  GET submit is always prevented. The Klaviyo network call only happens when a
  public key is configured (window.Digitaliz.klaviyo.publicKey).
*/
if (!window.DigitalizModules) window.DigitalizModules = {};
if (!window.DigitalizModules.KlaviyoForm) {
  const app = window.Digitaliz;
  const config = (app && app.klaviyo) || { strings: {} };
  const strings = config.strings || {};

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Lenient phone: digits only (after stripping separators), 7–15 long,
  // optional leading +. E.164 is the stricter subset used for phone_number.
  const PHONE_RE = /^\+?\d{7,15}$/;
  const E164_RE = /^\+\d{7,15}$/;

  class KlaviyoForm {
    constructor(form) {
      this.form = form;
      this.mode = form.getAttribute("data-klaviyo-form");
      this.submitBtn = form.querySelector('[type="submit"]');
      this.submitting = false;
      this.init();
    }

    init() {
      this.form.addEventListener("submit", (event) => this.onSubmit(event));
    }

    field(name) {
      return this.form.querySelector('[name="' + name + '"]');
    }

    value(name) {
      const el = this.field(name);
      return el ? el.value.trim() : "";
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.submitting) return;

      // Honeypot filled → bot. Silently stop.
      const honeypot = this.form.querySelector("[data-klaviyo-honeypot]");
      if (honeypot && honeypot.value) return;

      this.clearErrors();
      this.clearStatus();
      const errors = this.validate();
      if (errors.length) {
        this.showErrors(errors);
        return;
      }

      // Nothing to send to if Klaviyo isn't configured — validation still ran.
      if (!config.publicKey) return;

      const email = this.value("email");
      const consent = this.form.querySelector("[data-klaviyo-consent]");
      const phoneEl = this.field("phone");
      const phone = phoneEl ? phoneEl.value.replace(/[\s\-().]/g, "") : "";
      this.send({ email, phone, consentGiven: consent ? consent.checked : false });
    }

    validate() {
      const errors = [];

      // Any field marked required must be filled (checkboxes handled below).
      this.form.querySelectorAll("input[required], select[required], textarea[required]").forEach((el) => {
        if (el.type === "checkbox") return;
        if (!el.value.trim()) errors.push({ el: el, msg: strings.required });
      });

      // Email format (only when present and non-empty).
      const emailEl = this.field("email");
      if (emailEl && emailEl.value.trim() && !EMAIL_RE.test(emailEl.value.trim())) {
        errors.push({ el: emailEl, msg: strings.invalidEmail });
      }

      // Phone format (only when present and non-empty).
      const phoneEl = this.field("phone");
      if (phoneEl && phoneEl.value.trim()) {
        const cleaned = phoneEl.value.replace(/[\s\-().]/g, "");
        if (!PHONE_RE.test(cleaned)) errors.push({ el: phoneEl, msg: strings.invalidPhone });
      }

      // Required consent checkbox must be ticked.
      const consent = this.form.querySelector("[data-klaviyo-consent]");
      if (consent && consent.required && !consent.checked) {
        errors.push({ el: consent, msg: strings.consentRequired });
      }

      return errors;
    }

    async send({ email, phone, consentGiven }) {
      this.setLoading(true);
      if (app) app.emit("digitaliz:klaviyo:submit", { mode: this.mode });

      try {
        if (this.mode === "subscribe") {
          await this.subscribe(email, config.newsletterListId);
        } else {
          await this.recordEvent({ email, phone });
          // Every contact submitter joins the contact list, regardless of consent.
          if (config.contactListId) await this.subscribe(email, config.contactListId);
          // Ticking the newsletter box also adds them to the newsletter list.
          if (consentGiven && config.newsletterListId) await this.subscribe(email, config.newsletterListId);
        }
        this.form.reset();
        this.setStatus(strings.success, false);
        if (app) app.emit("digitaliz:klaviyo:success", { mode: this.mode });
      } catch (err) {
        this.setStatus(err && err.network ? strings.networkError : strings.genericError, true);
        if (app) app.emit("digitaliz:klaviyo:error", { mode: this.mode });
      } finally {
        this.setLoading(false);
      }
    }

    async subscribe(email, listId) {
      const payload = {
        data: {
          type: "subscription",
          attributes: {
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: email,
                  subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } }
                }
              }
            }
          },
          relationships: { list: { data: { type: "list", id: listId } } }
        }
      };
      await this.post(
        "https://a.klaviyo.com/client/subscriptions/?company_id=" + encodeURIComponent(config.publicKey),
        payload
      );
    }

    async recordEvent({ email, phone }) {
      const metric = this.form.getAttribute("data-klaviyo-metric") || "Form Submitted";
      const properties = { source: this.form.getAttribute("data-klaviyo-source") || "" };

      const message = this.value("message");
      const topic = this.value("topic");
      if (message) properties.message = message;
      if (topic) properties.topic = topic;
      if (phone) properties.phone = phone;

      const profile = { email: email };
      const first = this.value("first_name");
      const last = this.value("last_name");
      const full = this.value("full_name");
      if (first) profile.first_name = first;
      if (last) profile.last_name = last;
      if (!first && full) {
        const parts = full.split(" ").filter(Boolean);
        profile.first_name = parts.shift();
        if (parts.length) profile.last_name = parts.join(" ");
      }
      // Only use the reserved phone_number field for a proper E.164 value;
      // otherwise the number already rides along as a property so a loose
      // value can't make Klaviyo reject the whole event.
      if (phone && E164_RE.test(phone)) profile.phone_number = phone;

      const payload = {
        data: {
          type: "event",
          attributes: {
            properties: properties,
            metric: { data: { type: "metric", attributes: { name: metric } } },
            profile: { data: { type: "profile", attributes: profile } }
          }
        }
      };
      await this.post(
        "https://a.klaviyo.com/client/events/?company_id=" + encodeURIComponent(config.publicKey),
        payload
      );
    }

    async post(url, payload) {
      let res;
      try {
        res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", revision: config.revision },
          body: JSON.stringify(payload)
        });
      } catch (e) {
        const err = new Error("network");
        err.network = true;
        throw err;
      }
      if (!res.ok) {
        const err = new Error("request-failed");
        err.status = res.status;
        throw err;
      }
      return res;
    }

    setLoading(state) {
      this.submitting = state;
      this.form.classList.toggle("is-loading", state);
      if (this.submitBtn) this.submitBtn.disabled = state;
    }

    clearErrors() {
      this.form.classList.remove("has-error");
      this.form.querySelectorAll(".klaviyo-field-error").forEach((n) => n.remove());
      this.form.querySelectorAll('[aria-invalid="true"]').forEach((n) => n.removeAttribute("aria-invalid"));
    }

    showErrors(errors) {
      this.form.classList.add("has-error");
      let firstInput = null;
      errors.forEach((error) => {
        const input = error.el;
        if (!input) return;
        if (!firstInput) firstInput = input;
        input.setAttribute("aria-invalid", "true");
        const msg = document.createElement("span");
        msg.className = "klaviyo-field-error";
        msg.textContent = error.msg;
        (input.closest(".form-field, .footer-subscribe__field, .form-consent") || input.parentNode).appendChild(msg);
      });
      if (firstInput && firstInput.focus) firstInput.focus();
    }

    setStatus(message, isError) {
      clearTimeout(this.statusTimer);
      let status = this.form.querySelector(".klaviyo-form-status");
      if (!status) {
        status = document.createElement("p");
        status.className = "klaviyo-form-status";
        status.setAttribute("role", "status");
        status.setAttribute("aria-live", "polite");
        this.form.appendChild(status);
      }
      status.textContent = message || "";
      status.classList.toggle("is-error", !!isError);
      status.classList.toggle("is-success", !isError && !!message);
      // Auto-dismiss the success message after a minute; errors persist.
      if (!isError && message) {
        this.statusTimer = setTimeout(() => this.clearStatus(), 60000);
      }
    }

    clearStatus() {
      clearTimeout(this.statusTimer);
      const status = this.form.querySelector(".klaviyo-form-status");
      if (status) {
        status.textContent = "";
        status.classList.remove("is-error", "is-success");
      }
    }
  }

  window.DigitalizModules.KlaviyoForm = KlaviyoForm;
  document.querySelectorAll("[data-klaviyo-form]").forEach((form) => new KlaviyoForm(form));
}
